import Cookies from 'js-cookie';
import SuperAdminStorageService from '../services/SuperAdminStorageService';
import { LOGISTICS_RFQ_ROUTES } from '../constants/logisticsRfqRoutes';
import { getDashboardRoute } from './getDashboardRoute';
import { getAccessStaffDashboardRoute, isLogisticsStaffUser } from './roleSwitcher';

const AUTH_TOKEN = 'auth_token';

const SUBMITTED_RFQ_KIND_META = {
  'business-relocation': {
    breadcrumbCurrent: 'Business Relocation RFQ',
    listLabels: {
      superAdmin: 'Business Relocation RFQs',
      logisticsManager: 'Business Relocation RFQs',
      buyer: 'RFQs Sent',
    },
    paths: {
      superAdminList: '/super-admin/logistics/business-relocation-rfqs',
      logisticsManagerList: '/dashboard/logistics-manager/business-relocation-rfqs',
      buyerList: '/logistics-rfqs-sent',
    },
  },
  'exhibition-logistics': {
    breadcrumbCurrent: 'Exhibition Logistics RFQ',
    listLabels: {
      superAdmin: 'Exhibition Logistics RFQs',
      logisticsManager: 'Exhibition Logistics RFQs',
      buyer: 'RFQs Sent',
    },
    paths: {
      superAdminList: '/super-admin/logistics/exhibition-logistics-rfqs',
      logisticsManagerList: '/dashboard/logistics-manager/exhibition-logistics-rfqs',
      buyerList: '/logistics-rfqs-sent',
    },
  },
};

function normalizeSubmittedRfqPortal(value) {
  const portal = String(value || '').trim().toLowerCase();
  if (portal === 'super-admin' || portal === 'logistics-manager' || portal === 'buyer') {
    return portal;
  }
  return null;
}

function parseSearchParams(search = '') {
  return new URLSearchParams(
    String(search || '').startsWith('?') ? String(search).slice(1) : String(search || ''),
  );
}

function readReturnToFromSearch(search = '') {
  const raw = parseSearchParams(search).get('returnTo');
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  return trimmed;
}

function defaultListHrefForPortal(meta, portal) {
  if (portal === 'super-admin') return meta.paths.superAdminList;
  if (portal === 'logistics-manager') return meta.paths.logisticsManagerList;
  return meta.paths.buyerList;
}

function defaultListLabelForPortal(meta, portal) {
  if (portal === 'super-admin') return meta.listLabels.superAdmin;
  if (portal === 'logistics-manager') return meta.listLabels.logisticsManager;
  return meta.listLabels.buyer;
}

function dashboardHrefForPortal(portal, meta) {
  if (portal === 'super-admin') return '/super-admin/dashboard';
  if (portal === 'logistics-manager') {
    return getAccessStaffDashboardRoute() || meta.paths.logisticsManagerList;
  }
  return getDashboardRoute();
}

export function detectLogisticsSubmittedRfqPortal({
  pathname = '',
  search = '',
  state = null,
} = {}) {
  const fromState = normalizeSubmittedRfqPortal(state?.portal);
  if (fromState) return fromState;

  const fromQuery = normalizeSubmittedRfqPortal(parseSearchParams(search).get('portal'));
  if (fromQuery) return fromQuery;

  const normalizedPath = String(pathname || '');
  if (normalizedPath.startsWith('/super-admin/logistics/rfq/submitted')) {
    return 'super-admin';
  }

  if (
    normalizedPath.includes('/logistics/rfq/submitted/') &&
    isLogisticsStaffUser()
  ) {
    return 'logistics-manager';
  }

  if (SuperAdminStorageService.isAuthenticated()) {
    return 'super-admin';
  }

  if (isLogisticsStaffUser()) {
    return 'logistics-manager';
  }

  return 'buyer';
}

export function resolveLogisticsSubmittedRfqNavContext({
  kind,
  pathname = '',
  search = '',
  state = null,
} = {}) {
  const meta = SUBMITTED_RFQ_KIND_META[kind];
  if (!meta) return null;

  const portal = detectLogisticsSubmittedRfqPortal({ pathname, search, state });
  const defaultListHref = defaultListHrefForPortal(meta, portal);
  const returnToFromState =
    typeof state?.returnTo === 'string' && state.returnTo.trim().startsWith('/')
      ? state.returnTo.trim()
      : null;
  const returnToFromQuery = readReturnToFromSearch(search);
  const listHref = returnToFromState || returnToFromQuery || defaultListHref;

  return {
    portal,
    dashboardHref: dashboardHrefForPortal(portal, meta),
    dashboardLabel: 'Dashboard',
    listHref,
    listLabel: defaultListLabelForPortal(meta, portal),
    breadcrumbCurrent: meta.breadcrumbCurrent,
    showStaffBreadcrumb: portal === 'super-admin' || portal === 'logistics-manager',
  };
}

export function buildLogisticsSubmittedRfqNavigationState({
  portal,
  returnTo,
} = {}) {
  const next = {};
  const normalizedPortal = normalizeSubmittedRfqPortal(portal);
  if (normalizedPortal) next.portal = normalizedPortal;
  if (typeof returnTo === 'string' && returnTo.trim().startsWith('/')) {
    next.returnTo = returnTo.trim();
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Post-submit destination for logistics RFQ forms (public routes).
 * `/logistics/my-rfqs` is behind ProtectedRoute and sends super-admins to `/super-admin/dashboard`.
 */
export function getLogisticsRfqPostSubmitPath(rfqKind = 'generic') {
  if (SuperAdminStorageService.isAuthenticated()) {
    if (rfqKind === 'business-relocation') {
      return '/super-admin/logistics/business-relocation-rfqs';
    }
    if (rfqKind === 'exhibition-logistics') {
      return '/super-admin/logistics/exhibition-logistics-rfqs';
    }
    return '/super-admin/dashboard';
  }

  if (Cookies.get(AUTH_TOKEN)) {
    return '/logistics/my-rfqs';
  }

  return LOGISTICS_RFQ_ROUTES.selection;
}

/** Submitted RFQ detail — use super-admin prefix when SA is logged in (buyer route is behind ProtectedRoute). */
export function getLogisticsDedicatedSubmittedRfqPath(kind, id, options = {}) {
  const portal =
    options.portal ??
    (options.superAdmin || SuperAdminStorageService.isAuthenticated() ? 'super-admin' : 'buyer');
  const prefix = portal === 'super-admin' ? '/super-admin' : '';
  const base = `${prefix}/logistics/rfq/submitted/${encodeURIComponent(String(kind))}/${encodeURIComponent(String(id))}`;
  const params = new URLSearchParams();
  if (portal === 'logistics-manager') {
    params.set('portal', 'logistics-manager');
  }
  if (typeof options.returnTo === 'string' && options.returnTo.trim().startsWith('/')) {
    params.set('returnTo', options.returnTo.trim());
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}
